import { useEffect, useRef, useState } from 'preact/hooks';
import type { Content, TalentDef, TalentEffect } from '../../../data/schema';
import { buyTalent, nextCost, resetTalents } from '../../../core/meta/talents';
import type { Meta } from '../../../net/progress';
import { Padlock } from '../Padlock';
import { describeEffects } from '../talentText';
import { useEscape } from '../useEscape';

type Branch = 'commun' | 'xelor';
type NodeState = 'locked' | 'buyable' | 'bought' | 'maxed';

const BRANCHES: { id: Branch; label: string; note?: string }[] = [{ id: 'commun', label: 'Tronc commun' }, { id: 'xelor', label: 'Xelor', note: 'S\'applique au Xelor seulement' }];
const RESET_LABEL = 'Réinitialiser l\'arbre';
const CONFIRM_LABEL = 'Confirmer la réinitialisation';
const CONFIRM_MS = 5000;

/** Abréviation affichée dans le médaillon d'un nœud (pas d'icône dédiée aux talents). */
const TAG: Record<TalentEffect['type'], string> = {
  hpFlat: 'PV', hpPct: 'PV', damagePct: 'DG', kamasPct: 'K', rerolls: 'R', shopDiscountPct: '-%', startItem: 'OBJ', cooldownPct: 'CD', speedPct: 'VIT',
  xpPct: 'XP', startKamas: 'K+', pickupRange: 'AIM', shopRerollFree: 'R+', startSpell: 'SORT', rewindCooldown: 'REW', slowBonusPct: 'GEL',
  startLevel: 'NIV', refletsPct: 'RF', shieldOnRoom: 'BCL', bossDamagePct: 'BOSS',
};

const STATE_LABEL: Record<NodeState, string> = { locked: 'prérequis manquants', buyable: 'achetable', bought: 'acquis', maxed: 'rang maximum' };

/** Ordre de lecture et de navigation : palier puis abscisse. */
function ordered(content: Content, branch: Branch): TalentDef[] {
  return Object.values(content.talents).filter((t) => t.branch === branch).sort((a, b) => a.tier - b.tier || a.position.x - b.position.x);
}

function stateOf(def: TalentDef, content: Content, ranks: Record<string, number>, reflets: number): { state: NodeState; affordable: boolean; rank: number; cost: number } {
  const rank = ranks[def.id] ?? 0;
  const cost = nextCost(def, rank);
  if (!def.requires.every((r) => (ranks[r] ?? 0) >= 1 && content.talents[r])) return { state: 'locked', affordable: false, rank, cost };
  if (rank >= def.maxRank) return { state: 'maxed', affordable: false, rank, cost };
  const affordable = reflets >= cost;
  // un nœud déverrouillé mais trop cher reste « achetable » tant qu'il est au rang 0 (grisé), « acquis » sinon
  if (affordable || rank === 0) return { state: 'buyable', affordable, rank, cost };
  return { state: 'bought', affordable: false, rank, cost };
}

function nodeLabel(def: TalentDef, s: ReturnType<typeof stateOf>): string {
  const head = `${def.name}, rang ${s.rank} / ${def.maxRank}`;
  if (s.state === 'maxed') return `${head}, ${STATE_LABEL.maxed}`;
  const label = s.state === 'buyable' && !s.affordable ? 'pas assez de Reflets' : STATE_LABEL[s.state];
  return `${head}, coût ${s.cost} Reflets, ${label}`;
}

/** `onChange(apply)` : `apply` est appliquée à la méta du store (source de vérité), `null` si l'action n'y est plus possible. */
interface Props { meta: Meta; content: Content; onChange: (apply: (meta: Meta) => Meta | null) => void; onBack: () => void }

/** Panneau « Arbre de talents » du hub : deux onglets (tronc commun, Xelor), achat au clic, au toucher ou au clavier, réinitialisation confirmée. */
export function Talents({ meta, content, onChange, onBack }: Props) {
  const panel = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [branch, setBranch] = useState<Branch>('commun');
  const [selected, setSelected] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const list = ordered(content, branch);
  const inList = new Set(list.map((t) => t.id));
  const current = list.find((t) => t.id === selected) ?? list[0];
  const owned = Object.entries(meta.talents).some(([id, r]) => r > 0 && content.talents[id]);

  const clearTimer = () => { if (timer.current !== null) { clearTimeout(timer.current); timer.current = null; } };
  const nodeButtons = () => Array.from(panel.current?.querySelectorAll<HTMLButtonElement>('button.talent') ?? []);

  useEffect(() => {
    // focus initial : premier nœud achetable (sinon premier nœud)
    const first = list.find((t) => { const s = stateOf(t, content, meta.talents, meta.reflets); return s.state === 'buyable' && s.affordable; }) ?? list[0];
    if (first) panel.current?.querySelector<HTMLButtonElement>(`button.talent[data-talent="${first.id}"]`)?.focus({ preventScroll: true });
    return clearTimer;
  }, []);

  // Échap quitte même quand le focus est tombé sur le fond (mécanisme partagé avec les autres panneaux)
  useEscape(onBack);

  // l'affichage filtre les clics impossibles ; l'achat lui-même se calcule sur la méta du store (jamais sur une copie périmée)
  const buy = (id: string) => {
    if (!buyTalent(content.talents, meta.talents, id, meta.reflets)) return;
    onChange((m) => {
      const bought = buyTalent(content.talents, m.talents, id, m.reflets);
      return bought ? { ...m, talents: bought.ranks, reflets: bought.reflets, refletsSpent: m.refletsSpent + (m.reflets - bought.reflets) } : null;
    });
  };

  const onReset = () => {
    if (!confirming) {
      setConfirming(true);
      clearTimer();
      timer.current = setTimeout(() => { timer.current = null; setConfirming(false); }, CONFIRM_MS);
      return;
    }
    clearTimer();
    setConfirming(false);
    onChange((m) => {
      if (Object.keys(m.talents).length === 0) return null;
      const r = resetTalents(content.talents, m.talents, m.reflets);
      return { ...m, talents: r.ranks, reflets: r.reflets };
    });
  };

  const switchBranch = (b: Branch) => { setBranch(b); setSelected(null); };

  const onKeyDown = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('[role="tab"]') && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      const i = BRANCHES.findIndex((b) => b.id === branch);
      const to = BRANCHES[Math.min(BRANCHES.length - 1, Math.max(0, i + (e.key === 'ArrowRight' ? 1 : -1)))]!;
      switchBranch(to.id);
      requestAnimationFrame(() => panel.current?.querySelector<HTMLButtonElement>(`[role="tab"][data-branch="${to.id}"]`)?.focus());
      return;
    }
    const id = target?.closest<HTMLElement>('button.talent')?.dataset.talent;
    if (!id) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); // évite le clic natif (double achat) ; la répétition de touche n'achète pas en rafale
      if (!e.repeat) buy(id);
      return;
    }
    const buttons = nodeButtons();
    const from = buttons.findIndex((b) => b === document.activeElement);
    if (from < 0) return;
    let to: number;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = from + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') to = from - 1;
    else if (e.key === 'Home') to = 0;
    else if (e.key === 'End') to = buttons.length - 1;
    else return;
    e.preventDefault();
    if (to >= 0 && to < buttons.length && to !== from) buttons[to]!.focus();
  };

  // l'Espace déclenche le clic natif au relâchement : déjà traité à l'appui
  const onKeyUp = (e: KeyboardEvent) => { if (e.key === ' ' && (e.target as HTMLElement | null)?.closest('button.talent')) e.preventDefault(); };

  const states = new Map(list.map((t) => [t.id, stateOf(t, content, meta.talents, meta.reflets)]));
  const edges = list.flatMap((t) => t.requires.filter((r) => inList.has(r)).map((r) => ({ from: content.talents[r]!, to: t })));

  return (
    <div class="panel talents-panel" role="dialog" aria-label="Arbre de talents" ref={panel} onKeyDown={onKeyDown} onKeyUp={onKeyUp}>
      <div class="talents-head">
        <button class="talents-back" type="button" onClick={onBack}>Retour</button>
        <h2>Arbre de talents</h2>
        <span class="talents-reflets">Reflets Oniriques : <b>{meta.reflets}</b></span>
        <button class={`talents-reset${confirming ? ' is-confirming' : ''}`} type="button" disabled={!owned} onClick={onReset}>{confirming ? CONFIRM_LABEL : RESET_LABEL}</button>
      </div>
      <div class="talents-tabs" role="tablist" aria-label="Branches de talents">
        {BRANCHES.map((b) => (
          <button key={b.id} type="button" role="tab" id={`talents-tab-${b.id}`} data-branch={b.id} aria-selected={branch === b.id} aria-controls="talents-map" tabIndex={branch === b.id ? 0 : -1} class={branch === b.id ? 'is-active' : ''} onClick={() => switchBranch(b.id)}>{b.label}</button>
        ))}
      </div>
      {BRANCHES.find((b) => b.id === branch)?.note && <p class="talents-branch-note">{BRANCHES.find((b) => b.id === branch)!.note}</p>}
      <div class="talents-main">
        <div class="talents-body">
        <div class="talents-map" id="talents-map" role="tabpanel" aria-labelledby={`talents-tab-${branch}`}>
          <svg class="talents-edges" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            {edges.map(({ from, to }) => {
              const lit = (meta.talents[from.id] ?? 0) >= 1 && (meta.talents[to.id] ?? 0) >= 1;
              const open = !lit && (meta.talents[from.id] ?? 0) >= 1;
              return <line key={`${from.id}>${to.id}`} class={`talent-edge${lit ? ' is-lit' : open ? ' is-open' : ''}`} data-from={from.id} data-to={to.id} x1={from.position.x * 100} y1={from.position.y * 100} x2={to.position.x * 100} y2={to.position.y * 100} />;
            })}
          </svg>
          {list.map((t) => {
            const s = states.get(t.id)!;
            return (
              <button
                key={t.id}
                type="button"
                class={`talent is-${s.state}${current?.id === t.id ? ' is-selected' : ''}`}
                data-talent={t.id}
                data-affordable={s.state === 'buyable' ? String(s.affordable) : undefined}
                style={`left: ${t.position.x * 100}%; top: ${t.position.y * 100}%`}
                aria-label={nodeLabel(t, s)}
                aria-disabled={s.state === 'buyable' && s.affordable ? undefined : 'true'}
                onClick={() => { setSelected(t.id); buy(t.id); }}
                onFocus={() => setSelected(t.id)}
                onMouseEnter={() => setSelected(t.id)}
              >
                <span class="talent-medal">
                  {s.state === 'locked' ? <Padlock class="talent-lock" size={24} /> : <span class="talent-tag">{TAG[t.effects[0]!.type]}</span>}
                </span>
                {s.rank >= 1 && <span class="talent-rank">{s.rank}</span>}
                <span class="talent-name">{t.name}</span>
              </button>
            );
          })}
        </div>
        </div>
        {current && <Detail def={current} content={content} meta={meta} state={states.get(current.id)!} />}
      </div>
    </div>
  );
}

function Detail({ def, content, meta, state }: { def: TalentDef; content: Content; meta: Meta; state: ReturnType<typeof stateOf> }) {
  const missing = def.requires.filter((r) => (meta.talents[r] ?? 0) < 1).map((r) => content.talents[r]).filter((t): t is TalentDef => !!t);
  const cumulated = state.rank > 0 ? describeEffects(def.effects, state.rank, content).join(', ') : 'aucun';
  return (
    <aside class="talent-detail" aria-label="Détail du talent">
      <h3>{def.name}</h3>
      <p class="talent-desc">{def.description}</p>
      <p class="talent-rankline">Rang {state.rank} / {def.maxRank}</p>
      <p>Effet cumulé : <b>{cumulated}</b></p>
      {state.rank < def.maxRank
        ? <p>Prochain rang : <b>{state.cost} Reflets</b><span class="talent-next"> ({describeEffects(def.effects, 1, content).join(', ')})</span></p>
        : <p>Rang maximum atteint.</p>}
      {missing.length > 0 && <p class="missing">Requiert : {missing.map((t) => (t.branch === def.branch ? t.name : `${t.name} (tronc commun)`)).join(', ')}</p>}
    </aside>
  );
}
